/* Kbite (cartbite) back-in-stock integration for the quick-add modal.
 *
 * The app's own script only renders its "Notify me" button on product pages —
 * it resolves the product from window.location — so content loaded into
 * <quick-add-modal> never gets one. This file hooks the modal's product-info
 * load and variant changes and renders the app's button there via the
 * globals the app exposes for theme integration (window.initVar,
 * window.stockWidgetButtonParent). No-ops when the app isn't loaded.
 *
 * Caveat: initVar.renderStockWidget removes every existing notify button
 * document-wide before re-adding, so after the modal closes the main product
 * page's button is restored via the app's own onVariantChange().
 */

if (!window.kbiteQuickAddInitialized) {
  window.kbiteQuickAddInitialized = true;

  (() => {
    const productRequests = {};
    let renderQueue = Promise.resolve();

    const fetchProduct = (productUrl) => {
      if (!productRequests[productUrl]) {
        productRequests[productUrl] = fetch(`${productUrl}.js`).then((response) =>
          response.ok ? response.json() : null
        );
      }
      return productRequests[productUrl];
    };

    const waitForApp = async () => {
      for (let attempt = 0; attempt < 10; attempt += 1) {
        if (typeof window.initVar?.renderStockWidget === 'function') return window.initVar;
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      return null;
    };

    const renderNotifyButton = async (productInfo) => {
      const initVar = await waitForApp();
      if (!initVar) return;

      const buttonsWrapper = productInfo.querySelector('.product-form__buttons');
      const productUrl = (productInfo.dataset.url || '').split(/[?#]/)[0];
      if (!buttonsWrapper || !productUrl) return;

      const product = await fetchProduct(productUrl);
      // The modal may have been emptied while the product JSON was loading.
      if (!product || !buttonsWrapper.isConnected) return;

      const variantId = Number(
        productInfo.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value
      );
      const variant = product.variants.find((item) => item.id === variantId);
      if (!variant) return;

      const previousParent = window.stockWidgetButtonParent;
      const previousSelector = initVar.addToCartSelector;
      // Point the app at the modal: parent for the injected button, and the
      // modal's own submit button as the source of the classes it copies.
      window.stockWidgetButtonParent = buttonsWrapper;
      initVar.addToCartSelector = 'quick-add-modal .product-form__submit[name="add"]';
      try {
        await initVar.renderStockWidget(product, variant);
      } catch (error) {
        console.warn('kbite-quick-add: render failed', error);
      } finally {
        window.stockWidgetButtonParent = previousParent;
        initVar.addToCartSelector = previousSelector;
      }
    };

    // Serialize renders so a fast load + variant change can't interleave and
    // leave a duplicate button behind.
    const queueRender = (productInfo) => {
      renderQueue = renderQueue.then(() => renderNotifyButton(productInfo)).catch(() => {});
    };

    document.addEventListener('product-info:loaded', (event) => {
      const productInfo = event.target;
      if (productInfo?.tagName === 'PRODUCT-INFO' && productInfo.closest('quick-add-modal')) {
        queueRender(productInfo);
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        const productInfo = document.querySelector('quick-add-modal product-info');
        if (productInfo) queueRender(productInfo);
      });
    }

    document.body.addEventListener('modalClosed', () => {
      if (!window.location.pathname.includes('/products/')) return;
      window.initVar?.onVariantChange?.();
    });
  })();
}
